import { c as _c } from "react/compiler-runtime";
import type { ApiXTdhCollectionsPage } from "@/generated/models/ApiXTdhCollectionsPage";
import { EmptyState } from "@/components/common/EmptyState";
import { InlineRetry, ListError, ListMessage } from "../collection-tokens/subcomponents/XtdhTokensFallbacks";
import { XtdhReceivedCollectionCard } from "../collection-card-content";
type ApiXtdhCollection = Omit<ApiXTdhCollectionsPage["data"][number], "contract"> & {
  readonly contract?: string | null | undefined;
};
interface XtdhCollectionsListProps {
  readonly isEnabled: boolean;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly collections: ApiXTdhCollectionsPage["data"];
  readonly errorMessage?: string | undefined;
  readonly onRetry: () => void;
  readonly selectedContract?: string | null | undefined;
  readonly onSelectCollection?: ((contract: string | null) => void) | undefined | undefined;
  readonly isIdentityScoped?: boolean | undefined;
}
export function XtdhCollectionsList(t0) {
  const $ = _c(27);
  const {
    isEnabled,
    isLoading,
    isError,
    collections,
    errorMessage,
    onRetry,
    selectedContract,
    onSelectCollection,
    isIdentityScoped: t1
  } = t0;
  const isIdentityScoped = t1 === undefined ? true : t1;
  const targetLabel = isIdentityScoped ? "this identity" : "the ecosystem";
  if (!isEnabled) {
    let t2;
    if ($[0] !== targetLabel) {
      t2 = <ListMessage>Unable to load xTDH collections for {targetLabel}.</ListMessage>;
      $[0] = targetLabel;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  const showInitialLoading = isLoading && collections.length === 0;
  if (showInitialLoading) {
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <CollectionsSkeleton />;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  const showInitialError = isError && collections.length === 0;
  if (showInitialError) {
    const t2 = errorMessage ?? "Failed to load received collections.";
    let t3;
    if ($[3] !== onRetry || $[4] !== t2) {
      t3 = <ListError message={t2} onRetry={onRetry} />;
      $[3] = onRetry;
      $[4] = t2;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  if (!collections.length) {
    const t2 = isIdentityScoped ? "No xTDH received" : "No collections found";
    const t3 = isIdentityScoped ? "This identity will automatically accrue xTDH by holding eligible tokens from collections receiving grants." : "No xTDH collections to show yet. When grants are issued, collections receiving xTDH will appear here.";
    let t4;
    if ($[6] !== t2 || $[7] !== t3) {
      t4 = <EmptyState title={t2} message={t3} />;
      $[6] = t2;
      $[7] = t3;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    return t4;
  }
  let t2;
  if ($[9] !== selectedContract) {
    t2 = selectedContract?.trim().toLowerCase() ?? null;
    $[9] = selectedContract;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const normalizedSelected = t2;
  let t3;
  if ($[11] !== collections || $[12] !== normalizedSelected || $[13] !== onSelectCollection) {
    let t4;
    if ($[15] !== normalizedSelected || $[16] !== onSelectCollection) {
      t4 = (collection, index) => <XtdhReceivedCollectionCard key={getCollectionKey(collection, index)} collection={collection} onSelect={onSelectCollection} isSelected={normalizedSelected !== null && (collection.contract?.trim().toLowerCase() ?? null) === normalizedSelected} />;
      $[15] = normalizedSelected;
      $[16] = onSelectCollection;
      $[17] = t4;
    } else {
      t4 = $[17];
    }
    t3 = collections.map(t4);
    $[11] = collections;
    $[12] = normalizedSelected;
    $[13] = onSelectCollection;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[18] !== t3) {
    t4 = <ul className="tw-m-0 tw-p-0 tw-flex tw-flex-col tw-divide-y tw-divide-iron-800 tw-divide-x-0 tw-divide-solid">{t3}</ul>;
    $[18] = t3;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  let t5;
  if ($[20] !== errorMessage || $[21] !== isError || $[22] !== onRetry) {
    t5 = isError ? <InlineRetry message={errorMessage ?? "Unable to load more collections."} onRetry={onRetry} /> : null;
    $[20] = errorMessage;
    $[21] = isError;
    $[22] = onRetry;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  let t6;
  if ($[24] !== t4 || $[25] !== t5) {
    t6 = <div>{t4}{t5}</div>;
    $[24] = t4;
    $[25] = t5;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  return t6;
}
function CollectionsSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="tw-px-6 tw-pb-6"><ul className="tw-m-0 tw-flex tw-flex-col tw-gap-3 tw-p-0">{SKELETON_INDICES.map(_temp2)}</ul></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp2(index) {
  return <li key={`skeleton-${index}`} className="tw-list-none tw-animate-pulse tw-rounded-xl tw-border tw-border-iron-800 tw-bg-iron-900 tw-p-4"><div className="tw-flex tw-items-center tw-gap-3"><div className="tw-h-14 tw-w-14 tw-rounded-lg tw-bg-iron-800" /><div className="tw-flex-1 tw-space-y-2"><div className="tw-h-4 tw-w-32 tw-rounded tw-bg-iron-800" /><div className="tw-h-3 tw-w-48 tw-rounded tw-bg-iron-850" /></div></div><div className="tw-mt-4 tw-grid tw-gap-3 sm:tw-grid-cols-2 xl:tw-grid-cols-4">{SKELETON_METRIC_KEYS.map(_temp)}</div></li>;
}
function _temp(metricKey) {
  return <div key={metricKey} className="tw-space-y-2"><div className="tw-h-3 tw-w-20 tw-rounded tw-bg-iron-800" /><div className="tw-h-4 tw-w-24 tw-rounded tw-bg-iron-850" /></div>;
}
const SKELETON_INDICES = [0, 1, 2];
const SKELETON_METRIC_KEYS = Array.from({
  length: 9
}, (_, index) => `metric-${index}`);
function getCollectionKey(collection: ApiXtdhCollection, fallbackIndex: number) {
  const normalizedContract = collection.contract?.trim().toLowerCase();
  if (normalizedContract) {
    return normalizedContract;
  }
  return ["fallback", fallbackIndex, collection.xtdh, collection.xtdh_rate, collection.total_token_count, collection.active_token_count, collection.total_contributor_count, collection.active_contributor_count].join("-");
}